/**
 * Dashboard-wide OakGuard lockdown banner. Sits with OrgAlertBanner between
 * the header and page content, so every page shows it while anything on the
 * active team is locked down. Every team member sees it (tools are stopped
 * for all of them); owners and admins also get a link to manage it.
 *
 * Not dismissible: an active emergency stop shouldn't be hideable. Hidden on
 * the oakGuard page itself, which shows the full per-lock banners with the
 * Switch mode / Lift controls.
 */

import { useQuery } from '@tanstack/react-query';
import { Link, useLocation } from 'react-router';
import { ShieldAlert } from 'lucide-react';
import { api } from '@/lib/api';
import type { MeResponse } from '@oakallow/shared';
import { useLockdownStatus } from '@/lib/use-lockdown-status';

const LOCK_ACCENT = '#9b2242';

export default function LockdownBanner() {
  const { pathname } = useLocation();
  const status = useLockdownStatus();
  // Same query key RequireAuth uses, so this reads the cached /api/me.
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: () => api<MeResponse>('/api/me'), staleTime: 5 * 60 * 1000 });

  if (!status?.active || pathname === '/dashboard/oakguard') return null;

  const canManage = me?.team_role === 'owner' || me?.team_role === 'admin';
  const scope = status.team
    ? 'all orgs on this team'
    : status.orgs.map((o) => o.name || o.external_id).join(', ');
  const lock = status.team || status.orgs[0]?.lock;

  return (
    <div
      role="alert"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        flexWrap: 'wrap',
        padding: '0.65rem 1rem',
        background: 'rgba(155, 34, 66, 0.12)',
        borderBottom: `1px solid ${LOCK_ACCENT}`,
        color: 'var(--text-primary)',
        fontSize: '0.875rem',
        lineHeight: 1.4,
      }}
    >
      <ShieldAlert size={16} style={{ flexShrink: 0, color: LOCK_ACCENT }} />
      <span style={{ flex: '1 1 240px', minWidth: 0 }}>
        <strong>Lockdown active. No tools will run in {scope}.</strong>
        {lock && (
          <span style={{ color: 'var(--text-secondary)' }}>
            {' '}{lock.by_email ? `Locked by ${lock.by_email}` : 'Locked'}
            {lock.reason ? `: ${lock.reason}` : ''}.
            {status.orgs.length + (status.team ? 1 : 0) > 1 ? ' More than one lock is active.' : ''}
          </span>
        )}
      </span>
      {canManage && (
        <Link to="/dashboard/oakguard" style={{ color: LOCK_ACCENT, textDecoration: 'underline', fontWeight: 600, whiteSpace: 'nowrap' }}>
          Manage on oakGuard →
        </Link>
      )}
    </div>
  );
}
